import React, { type FC, useCallback, useMemo } from 'react';
import { Formik, Form, type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type UiHStackProps,
  UiStack,
  UiButton,
  UiText,
  UiLink,
  uiStyles,
  UiHStack, UiIconInfo, UiIconCaretRight, UiBox, UiIconCaretLeft,
} from '@/lib/ui';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import BaseFormCheckboxListField, { type CheckboxOption } from '@/base/Form/CheckboxListField';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { IsRequiredType, type DiscountCode, type Registration, type RegistrationUpdateRequest, updateRegistration, type AttendeeCategoryData, registrationQueryKey, checkAnyDiscounts, discountCodeAnyQueryKey, AnyDiscountResponse, cartQueryKey } from '@/api/registration';
import CheckboxField from '@/base/Form/CheckboxField';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import DiscountCodeForm from './DiscountCodeForm';
import { type ApiResponse, type ApiResponseSingle } from '@/api/tenantClient';
import { type FileOrUrl } from '@/base/ImageSelect/ImageSelectItem';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { registration as registrationAPI } from '@/api';
import { toBase64 } from '@/lib/util';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import InfoBox from '@/registration/component/InfoBox';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import PolicyCheckBox from '@/registration/component/Register/PolicyCheckBox';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';
import BaseThemeButton from '@/base/ThemeButton';

export interface CategoryFormProps extends UiHStackProps {
  attendeeCategoryId: number | undefined
  attendeeCategoryData: AttendeeCategoryData[] | undefined
}

export interface FormData {
  attendeeCategory: string | undefined
  discountCodeString?: string
  discountCode?: DiscountCode
  idPhotocopy: FileOrUrl[]
  idInfo?: string
  isPrivacyPolicyAgreed: boolean
  isTermsAndConditionsAgreed: boolean
  // strict value
  privacyPolicyUrl?: string
  termsAndConditionsUrl?: string
  isDisabledDiscountCode?: boolean
}

const initFormData: FormData = {
  attendeeCategory: '',
  idInfo: '',
  idPhotocopy: [],
  isPrivacyPolicyAgreed: false,
  isTermsAndConditionsAgreed: false
};

const formSchema = Yup.object().shape({
  privacyPolicyUrl: Yup.string().nullable(),
  termsAndConditionsUrl: Yup.string().nullable(),
  discountCodeString: Yup.string().nullable(),
  discountCode: Yup.object({
    id: Yup.string(),
    discountCode: Yup.string(),
    idPhotocopy: Yup.string()
  }).nullable(),
  idPhotocopyBase64: Yup.string(),
  attendeeCategory: Yup.string().required('Please choose an attendee group'),
  isPrivacyPolicyAgreed: Yup.boolean().when('privacyPolicyUrl', {
    is: (value: string) => { return (!!value); },
    then: (schema) => { return schema.isTrue('Please agree with the privacy policy before you continue'); }
  }),
  isTermsAndConditionsAgreed: Yup.boolean().when('termsAndConditionsUrl', {
    is: (value: string) => { return (!!value); },
    then: (schema) => { return schema.isTrue('Please agree with terms and condition before you continue'); }
  }),
  idPhotocopy: Yup.array()
    .when('discountCode', {
      is: (discountCode: DiscountCode | undefined) => { return (discountCode?.idPhotocopy === IsRequiredType.REQUIRED); },
      then: (schema) => { return schema.min(1, 'ID photocopy is required'); }
    }),
  idInfo: Yup.string()
    .when('discountCode', {
      is: (discountCode: DiscountCode | undefined) => { return (discountCode?.idInfo === IsRequiredType.REQUIRED); },
      then: (schema) => { return schema.required('ID Info is required'); }
    })
});

const CategoryForm: FC<CategoryFormProps> = ({
  attendeeCategoryId,
  attendeeCategoryData
}) => {
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest } = useTenantApi();
  const eventSettingQuery = useEventSettingsQuery(eventId);
  const { userSession } = useUserSession();
  const registrationQuery = useRegistrationQuery(eventId);
  const { toNextStepWithSteps } = useRegisterSteps();

  const options: CheckboxOption[] = (Array.isArray(attendeeCategoryData) ? attendeeCategoryData : []).filter((attendeeCategory) => {
    if (attendeeCategoryId) {
      return Number(attendeeCategoryId) === attendeeCategory.id;
    } else {
      return attendeeCategory.isVisible;
    }
  }).map((attendeeCategory) => {
    return {
      value: `${attendeeCategory.id}`,
      label: attendeeCategory.name,
      description: attendeeCategory.description,
    };
  }) ?? [];

  const anyDiscountsQuery = useQuery<boolean, Error>({
    queryKey: [discountCodeAnyQueryKey, { eventId }],
    queryFn: async () => {
      const result = await checkAnyDiscounts(createTenantApiRequest)({ eventId });
      return result.item.any;
    }
  });

  const { mutateAsync: updateRegistrationMutate, isLoading: isUpdateRegistrationLoading, error } = useMutation<ApiResponseSingle<Registration>, Error, RegistrationUpdateRequest>({
    mutationFn: async (data: RegistrationUpdateRequest) => {
      return await updateRegistration(createTenantApiRequest)(data);
    },
    onSuccess: (data: ApiResponseSingle<Registration>) => {
      if (data.errors && data.errors.length > 0) {
        throw new Error(data.errors.join(', '));
      }
      void queryClient.invalidateQueries({ queryKey: [registrationQueryKey, { eventId }, { token: userSession?.authToken }] });
    },
    onError: (registrationError: any) => {
      throw registrationError;
    }
  });

  const { mutateAsync: checkDiscountCode, isLoading: isCheckingDiscountCode } = useMutation<ApiResponse<DiscountCode>, Error, string>({
    mutationFn: async (code: string) => {
      return await registrationAPI.checkDiscountCode(createTenantApiRequest)({ eventId, discountCode: code });
    }
  });

  const onSubmit = useCallback(
    async (
      values: FormData,
      { setSubmitting }: FormikHelpers<FormData>
    ) => {
      setSubmitting(true);
      if (!registrationQuery.data) {
        throw new Error('Registration not found');
      }
      const data = await updateRegistrationMutate({
        registration: {
          id: registrationQuery.data.id,
          attendeeCategoryId: Number(values.attendeeCategory),
          tmpIdPhotocopy: values.idPhotocopy[0] instanceof File ? await toBase64(values.idPhotocopy[0]) : values.idPhotocopy[0],
          idInfo: values.idInfo,
          termsAndConditionsAccepted: values.isTermsAndConditionsAgreed,
          privacyPolicyAccepted: values.isPrivacyPolicyAgreed,
          ...values.discountCode ? { discountId: values.discountCode.id } : {}
        }
      });
      setSubmitting(false);
      // Reload cart - check added item available for the selected attendee category or not
      const registrationId = registrationQuery.data.id;
      void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId }] });
      toNextStepWithSteps(data.item.registrationSteps);
    }, [registrationQuery.data, toNextStepWithSteps, updateRegistrationMutate]);

  return (
    <QueryContainer query={eventSettingQuery}>
      {(eventSetting) => {

        const attendeeCategoryLabel = `${eventSetting?.pageMetadata?.attendeeCategory?.title || 'Attendee group'}`;
        return (
          <QueryContainer query={anyDiscountsQuery}>
            {(anyDiscounts) => {
              return (
                <Formik<FormData>
                  initialValues={{
                    ...initFormData,
                    privacyPolicyUrl: eventSetting.privacy?.privacyPolicyUrl,
                    termsAndConditionsUrl: eventSetting.privacy?.termsAndConditionsUrl,
                    attendeeCategory: attendeeCategoryId ? attendeeCategoryId.toString() : (registrationQuery?.data?.attendeeCategoryId ? registrationQuery?.data?.attendeeCategoryId.toString() : undefined),
                    discountCodeString: registrationQuery?.data?.discount?.discountCode ?? undefined,
                    discountCode: registrationQuery?.data?.discount ?? undefined,
                    idPhotocopy: registrationQuery?.data?.tmpIdPhotocopy ? [registrationQuery?.data?.tmpIdPhotocopy] : [],
                    idInfo: registrationQuery?.data?.idInfo ?? '',
                    isPrivacyPolicyAgreed: registrationQuery?.data?.privacyPolicyAccepted ?? false,
                    isTermsAndConditionsAgreed: registrationQuery?.data?.termsAndConditionsAccepted ?? false,
                    isDisabledDiscountCode: false
                  }}
                  validateOnChange={false}
                  validateOnBlur={false}
                  validationSchema={formSchema}
                  onSubmit={onSubmit}
                  enableReinitialize
                >
                  {(form) => {
                    const disabledNextForDiscount = (form.values.isDisabledDiscountCode && Boolean(form.values.discountCodeString));
                    return <Form style={{ display: 'flex', flexDirection: 'column' }}>
                      <BaseTransitionSlideLeft>
                        <BaseFormFieldGroup>
                          {eventSetting?.pageMetadata?.attendeeCategory?.description && (
                            <UiStack spacing={4}>
                              <InfoBox
                                title={attendeeCategoryLabel}
                                description={eventSetting?.pageMetadata?.attendeeCategory?.description}
                              />
                            </UiStack>
                          )}
                          {error && (
                            <UiStack spacing={4} flexGrow={1} py={4}>
                              <BaseMessageBarError>
                                {error.message ?? 'Failed to save the host.'}
                              </BaseMessageBarError>
                            </UiStack>
                          )}
                          {(options.length > 0 ? (
                            <BaseFormCheckboxListField
                              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                              label={attendeeCategoryLabel}
                              name={'attendeeCategory'}
                              options={options}
                              isMultiple={false}
                              layout={'stack'}
                              disabled={(!!userSession?.authToken && registrationQuery?.data?.paid) ?? false}
                            />
                          ) : (
                            <BaseMessageBarInfo borderRadius={uiStyles.borderRadius} bgColor={'red.100'}>
                              No {attendeeCategoryLabel.toLowerCase()} available.
                            </BaseMessageBarInfo>
                          ))}
                          {anyDiscounts && (
                            <DiscountCodeForm
                              existingDiscountCode={registrationQuery?.data?.discount}
                              onCheckDiscountCode={checkDiscountCode}
                              setDisabledDiscountCode={form.setFieldValue as (field: string, value: any, shouldValidate?: boolean) => Promise<void>}
                            />
                          )}
                          <BaseDividerHorizontal height={1} />
                          <UiStack spacing={4} pb={4}>
                            {!!eventSetting?.privacy.privacyPolicyUrl && (
                              <PolicyCheckBox checkboxName='isPrivacyPolicyAgreed' policyText={'Privacy Policy'} policyUrl={eventSetting.privacy.privacyPolicyUrl} />
                            )}
                            {!!eventSetting?.privacy.termsAndConditionsUrl && (
                              <PolicyCheckBox checkboxName='isTermsAndConditionsAgreed' policyText={'Terms and Conditions'} policyUrl={eventSetting.privacy.termsAndConditionsUrl} />
                            )}
                          </UiStack>
                        </BaseFormFieldGroup>
                      </BaseTransitionSlideLeft>
                      <UiStack height={96} />
                      <LayoutFooter>
                        <UiHStack justifyContent={'flex-end'} flexGrow={1}>
                          {/*<UiButton*/}
                          {/*  px={8}*/}
                          {/*  size={'lg'}*/}
                          {/*  shadow={'base'}*/}
                          {/*  colorScheme={'primary'}*/}
                          {/*  type={'submit'}*/}
                          {/*  isDisabled={(!attendeeCategoryId && options.length === 0) || disabledNextForDiscount}*/}
                          {/*  isLoading={isCheckingDiscountCode || isUpdateRegistrationLoading}*/}
                          {/*>*/}
                          {/*  Next*/}
                          {/*</UiButton>*/}
                          <UiStack w={24}/>
                          <BrandFooter/>
                          <BaseThemeButton
                            theme={'primary'}
                            type={'submit'}
                            isDisabled={(!attendeeCategoryId && options.length === 0) || disabledNextForDiscount}
                            rightIcon={(
                              <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                            )}
                          >
                            Next
                          </BaseThemeButton>
                          {/*<UiButton*/}
                          {/*  size={'lg'}*/}
                          {/*  colorScheme={'primary'}*/}
                          {/*  type={'submit'}*/}
                          {/*  isDisabled={(!attendeeCategoryId && options.length === 0) || disabledNextForDiscount}*/}
                          {/*  variant={'ghost'}*/}
                          {/*  fontSize={'lg'}*/}
                          {/*  rightIcon={(*/}
                          {/*    <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                          {/*  )}*/}
                          {/*>*/}
                          {/*  Next*/}
                          {/*</UiButton>*/}
                        </UiHStack>
                      </LayoutFooter>
                    </Form>;
                  }}
                </Formik>
              );
            }}
          </QueryContainer>
        );
      }}
    </QueryContainer>
  );
};

export default CategoryForm;
